import { useState, useEffect, useCallback } from 'react';
import { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Link2, Palette, Download, Camera, Wallet,
  Sparkles, Smartphone, Shield, Moon,
} from 'lucide-react';
import AdBanner from '../components/AdBanner';
import SectionHeader from '../components/SectionHeader';
import StatsBanner from '../components/StatsBanner';
import styles from './Home.module.css';

// ── Lazy load heavy tools ─────────────────────────────────────
const QRGenerator = lazy(() => import('../components/QRGenerator'));
const QRScanner   = lazy(() => import('../components/QRScanner'));

// ── Static data ───────────────────────────────────────────────
const FEATURES = [
  { icon: Link2,      title: '16 QR Types',        desc: 'URL, UPI, WiFi, WhatsApp, vCard, Instagram, YouTube, Email, Phone, Location, SMS and more.' },
  { icon: Palette,    title: 'Full Customization',  desc: 'Custom colors, body shapes, marker shapes, logo upload and transparent background.' },
  { icon: Download,   title: '4 Download Formats',  desc: 'Download as PNG, JPG or SVG. Copy to clipboard or print directly from browser.' },
  { icon: Camera,     title: 'AI Scanner',          desc: 'Scan QR codes via camera or upload image. AI explains content + Google Safe Browsing check.' },
  { icon: Wallet,     title: 'UPI QR for India',    desc: 'Generate UPI payment QR for PhonePe, GPay, Paytm, BHIM. Test before printing.' },
  { icon: Sparkles,   title: 'AI Quality Score',    desc: 'AI rates your QR 0-100 and suggests fixes before you download.' },
  { icon: Smartphone, title: 'WhatsApp Stories',    desc: 'Create beautiful story cards with your QR code for WhatsApp Status and Instagram.' },
  { icon: Shield,     title: '100% Private',        desc: 'All QR generation happens in your browser. No data is ever sent to any server.' },
  { icon: Moon,       title: 'Dark & Light Mode',   desc: 'Switch themes freely. Your preference is saved automatically on your device.' },
];

const STEPS = [
  { n: '1', title: 'Choose Type',    desc: 'Select URL, UPI, WiFi, WhatsApp or any of 16 types.' },
  { n: '2', title: 'Enter Details',  desc: 'Fill in your info. QR updates live as you type.' },
  { n: '3', title: 'Customize',      desc: 'Pick colors, shapes and size. Add your logo.' },
  { n: '4', title: 'Download Free',  desc: 'Save as PNG, JPG or SVG. Share via WhatsApp.' },
];

const TESTIMONIALS = [
  { name: 'Rahul Sharma',   role: 'Shop Owner',        city: 'Delhi',     avatar: '👨‍💼', stars: 5, text: 'Best free QR code generator in India! The UPI support is amazing. Generated my payment QR in 30 seconds and it works with PhonePe and GPay perfectly.' },
  { name: 'Priya Patel',    role: 'Restaurant Owner',  city: 'Mumbai',    avatar: '👩‍🍳', stars: 5, text: 'Replaced all my paper menus with QR codes. No watermarks, no signup, completely free. My customers love it and it saves me printing costs every month.' },
  { name: 'Arjun Singh',    role: 'Freelance Designer', city: 'Bangalore', avatar: '👨‍💻', stars: 5, text: 'SVG download is excellent for print work. I use SimplifyQR for all client projects. Clean, fast and completely free.' },
  { name: 'Sneha Reddy',    role: 'College Student',   city: 'Hyderabad', avatar: '👩‍🎓', stars: 4, text: 'Used for our college fest — WiFi QR codes and event registration. The dark mode and AI quality checker are nice touches!' },
  { name: 'Mohammed Ali',   role: 'Street Food Vendor', city: 'Chennai',  avatar: '🧑‍🍴', stars: 5, text: 'Mere paas koi technical knowledge nahi thi. SimplifyQR se 2 minute mein apna UPI QR banaya. Ab mera business cashless ho gaya!' },
];

const FAQS = [
  { q: 'Is SimplifyQR completely free?',        a: 'Yes. SimplifyQR is 100% free forever. No signup, no watermarks, no download limits. All features are free for personal and commercial use.' },
  { q: 'Can I generate UPI QR codes for free?', a: 'Yes. Generate UPI payment QR codes for PhonePe, Google Pay, Paytm, BHIM and all UPI apps completely free. Just enter your UPI ID and download.' },
  { q: 'Is my data safe on SimplifyQR?',        a: 'Yes. All QR generation happens in your browser. No data is ever sent to any server. Your UPI IDs and WiFi passwords never leave your device.' },
  { q: 'What formats can I download in?',       a: 'Download as PNG, JPG or SVG. SVG is recommended for printing as it scales to any size without losing quality.' },
  { q: 'Does it work on mobile phones?',        a: 'Yes. SimplifyQR works on all Android and iPhone devices. The camera scanner also works directly in your mobile browser.' },
  { q: 'How do I create a QR for my shop?',     a: 'Select UPI, enter your UPI ID like yourname@paytm, enter your shop name and download. Print and place at your counter to accept payments.' },
];

const BLOG_PREVIEW = [
  { icon: '💳', tag: 'India',    title: 'How to Generate UPI QR Code Free in India',         excerpt: 'Generate a UPI QR code for your business and start accepting payments from PhonePe, GPay, Paytm and all UPI apps.',      date: '27 Apr 2026', read: '5 min' },
  { icon: '🏪', tag: 'Business', title: 'How to Use QR Codes for Your Restaurant Menu',       excerpt: 'Replace physical menus with QR codes — faster, more hygienic, and easy to update anytime without reprinting.',           date: '15 Apr 2026', read: '4 min' },
  { icon: '📶', tag: 'Tips',     title: 'Free WiFi QR Code Generator — Share Password Easily', excerpt: 'Stop typing long passwords. Generate a WiFi QR code once and let guests connect with a single scan.',                  date: '8 Apr 2026',  read: '3 min' },
];

const QUICK_TYPES = [
  { type: 'upi',       label: 'UPI Payment' },
  { type: 'wifi',      label: 'WiFi QR' },
  { type: 'whatsapp',  label: 'WhatsApp QR' },
  { type: 'url',       label: 'URL QR' },
];

const TRUST_LOGOS = ['PhonePe', 'Google Pay', 'Paytm', 'BHIM', 'WhatsApp'];

// ── Page loader ───────────────────────────────────────────────
const ToolLoader = () => (
  <div style={{
    minHeight: 400, display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexDirection: 'column', gap: '1rem', color: 'var(--text2)'
  }}>
    <div style={{
      width: 40, height: 40,
      border: '3px solid var(--border)',
      borderTopColor: 'var(--accent)',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    <span style={{ fontSize: '0.85rem' }}>Loading tool...</span>
  </div>
);

// ── Decorative QR preview (pure CSS grid) ─────────────────────
const QR_CELLS = [
  1,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,1,1,
  1,0,0,0,0,0,1,0,0,1,0,1,0,0,0,0,0,1,
  1,0,1,1,1,0,1,0,1,1,0,1,0,1,1,1,0,1,
  1,0,1,1,1,0,1,0,0,0,1,1,0,1,1,1,0,1,
  1,0,1,1,1,0,1,0,1,0,1,0,0,1,1,1,0,1,
  1,0,0,0,0,0,1,0,1,1,0,1,0,0,0,0,0,1,
  1,1,1,1,1,1,1,0,1,0,1,0,1,1,1,1,1,1,
  0,0,0,0,0,0,0,0,1,1,0,1,0,0,0,0,0,0,
  1,0,1,1,0,1,1,1,0,0,1,0,1,1,0,1,1,0,
  0,1,0,0,1,0,0,1,1,1,0,1,0,0,1,0,1,1,
  1,1,1,0,1,1,1,0,0,1,1,0,1,1,0,1,0,0,
  0,0,1,1,0,0,1,1,1,0,0,1,0,1,1,0,1,1,
  1,1,0,0,1,1,0,0,1,1,0,1,1,0,0,1,0,0,
  0,0,0,0,0,0,0,0,1,0,1,0,1,1,0,0,1,1,
  1,1,1,1,1,1,1,0,0,1,0,1,1,0,1,1,0,0,
  1,0,0,0,0,0,1,0,1,1,1,0,0,1,0,1,1,1,
  1,0,1,1,1,0,1,0,0,1,0,1,1,0,1,0,0,1,
  1,0,1,1,1,0,1,0,1,0,1,1,0,1,1,1,0,1,
];

// ── Main Component ────────────────────────────────────────────
export default function Home() {
  const [activeTool, setActiveTool] = useState('generator');

  // ── Unified scroll helper ─────────────────────────────────
  const scrollToTool = useCallback(() => {
    requestAnimationFrame(() => {
      document.getElementById('tool')?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  // ── Switch to QR type ─────────────────────────────────────
  const openType = useCallback((type) => {
    setActiveTool('generator');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('setQRType', { detail: type }));
        scrollToTool();
      });
    });
  }, [scrollToTool]);

  // ── Single merged useEffect ───────────────────────────────
  useEffect(() => {
    const handleOpenGenerator = () => { setActiveTool('generator'); scrollToTool(); };
    const handleOpenScanner   = () => { setActiveTool('scanner');   scrollToTool(); };

    window.addEventListener('openGenerator', handleOpenGenerator);
    window.addEventListener('openScanner',   handleOpenScanner);

    // Handle hash
    if (window.location.hash === '#scanner')   { setActiveTool('scanner');   scrollToTool(); }
    if (window.location.hash === '#generator') { setActiveTool('generator'); scrollToTool(); }

    // Handle landing type from other pages
    const landingType = localStorage.getItem('simplifyqr_landing_type');
    if (landingType) {
      localStorage.removeItem('simplifyqr_landing_type');
      if (landingType === 'scanner') {
        setActiveTool('scanner');
        scrollToTool();
      } else {
        openType(landingType);
      }
    }

    // Auto scroll on mobile
    if (window.innerWidth < 768) {
      setTimeout(scrollToTool, 1500);
    }

    return () => {
      window.removeEventListener('openGenerator', handleOpenGenerator);
      window.removeEventListener('openScanner',   handleOpenScanner);
    };
  }, [scrollToTool, openType]);

  return (
    <>
      <Helmet>
        <title>Free QR Code Generator India — UPI, WiFi, WhatsApp | SimplifyQR</title>
        <meta name="description" content="Free QR code generator for India. Create UPI payment QR, WhatsApp QR, WiFi QR and 13 more types. No watermark, no signup. Download PNG JPG SVG instantly." />
        <meta name="keywords" content="free qr code generator, qr code generator india, online qr code scanner, upi qr code generator, wifi qr code generator, whatsapp qr code generator, create qr code online, qr code for upi payment, free qr code no watermark" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href="https://simplifyqr.com/" />
        <meta property="og:title"       content="Free QR Code Generator & Scanner Online — SimplifyQR" />
        <meta property="og:description" content="Generate free QR codes for UPI payment, WiFi, WhatsApp, URL, vCard and more. No signup. No watermark. Made for India." />
        <meta property="og:type"        content="website" />
        <meta property="og:url"         content="https://simplifyqr.com/" />
        <meta property="og:image"       content="https://simplifyqr.com/og-image.png" />
        <meta name="twitter:card"       content="summary_large_image" />
        <meta name="twitter:title"      content="Free QR Code Generator & Scanner — SimplifyQR" />
        <meta name="twitter:description" content="Generate QR codes for URL, UPI, WiFi, WhatsApp instantly. Free forever. No signup." />
        <meta name="twitter:image"      content="https://simplifyqr.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "SimplifyQR",
          "url": "https://simplifyqr.com",
          "description": "Free QR code generator and scanner. Generate QR codes for URL, UPI payment, WiFi, WhatsApp, vCard and more. No signup. No watermark.",
          "applicationCategory": "UtilitiesApplication",
          "operatingSystem": "Any",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroGlow} aria-hidden="true" />
        <div className={`container ${styles.heroInner}`}>
          {/* Left: copy */}
          <div className={styles.heroCopy}>
            <div className={styles.heroBadges}>
              {['Free Forever', 'No Signup', 'No Watermark'].map(b => (
                <span key={b} className={styles.heroBadge}>
                  <span className={styles.heroBadgeDot} aria-hidden="true" />
                  {b}
                </span>
              ))}
            </div>

            <h1 className={styles.heroTitle}>
              Create a Free QR Code{' '}
              <span className={styles.heroAccent}>in 5 Seconds</span>
            </h1>

            <p className={styles.heroSub}>
              The simplest QR code generator for India — UPI, WiFi, WhatsApp and
              13 more types. Customize, download in PNG / SVG and share.
              No signup, no watermark, ever.
            </p>

            <div className={styles.heroBtns}>
              <button className="btn-primary btn-large" onClick={() => { setActiveTool('generator'); scrollToTool(); }}>
                Generate QR Code Free
              </button>
              <button className="btn-outline btn-large" onClick={() => { setActiveTool('scanner'); scrollToTool(); }}>
                Scan a QR Code
              </button>
            </div>

            {/* Quick type shortcuts */}
            <div className={styles.heroHint}>
              <span className={styles.heroHintLabel}>Popular:</span>
              {QUICK_TYPES.map((t) => (
                <button key={t.type} className={styles.heroChip} onClick={() => openType(t.type)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right: QR preview card */}
          <div className={styles.heroVisual} aria-hidden="true">
            <div className={styles.qrCard}>
              <div className={styles.qrCardTop}>
                <span className={styles.qrDot} />
                <span className={styles.qrDot} />
                <span className={styles.qrDot} />
              </div>
              <div className={styles.qrGrid}>
                {QR_CELLS.map((c, i) => (
                  <span key={i} className={c ? styles.qrOn : styles.qrOff} />
                ))}
              </div>
              <div className={styles.qrCardFoot}>
                <span className={styles.qrCardLabel}>simplifyqr.com</span>
                <span className={styles.qrCardScan}>Scan me</span>
              </div>
            </div>
            <div className={`${styles.floatPill} ${styles.floatPillA}`}>UPI ready</div>
            <div className={`${styles.floatPill} ${styles.floatPillB}`}>PNG · SVG · JPG</div>
          </div>
        </div>

        {/* Trust logos */}
        <div className={`container ${styles.trustRow}`}>
          <span className={styles.trustLabel}>Works with</span>
          {TRUST_LOGOS.map(l => (
            <span key={l} className={styles.trustLogo}>{l}</span>
          ))}
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <StatsBanner />

      {/* ── QUICK HOW ── */}
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className={styles.quickHow}>
          {[
            { num: '1', title: 'Choose type',   desc: 'URL, UPI, WiFi...' },
            { num: '2', title: 'Enter details', desc: 'Takes 10 seconds' },
            { num: '3', title: 'Download free', desc: 'PNG, JPG or SVG' },
          ].map(s => (
            <div key={s.title} className={styles.quickHowItem}>
              <span className={styles.quickHowNum}>{s.num}</span>
              <div className={styles.quickHowText}>
                <span className={styles.quickHowTitle}>{s.title}</span>
                <span className={styles.quickHowDesc}>{s.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── AD TOP ── */}
      <div className="container">
        <AdBanner slot="TOP_BANNER_SLOT" format="horizontal" />
      </div>

      {/* ── MAIN TOOL ── */}
      <div className="container section" id="tool">
        <div className={styles.toolTabs}>
          <button
            className={`${styles.toolTab} ${activeTool === 'generator' ? styles.toolTabActive : ''}`}
            onClick={() => setActiveTool('generator')}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7z"/>
            </svg>
            QR Generator
          </button>
          <button
            className={`${styles.toolTab} ${activeTool === 'scanner' ? styles.toolTabActive : ''}`}
            onClick={() => setActiveTool('scanner')}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M4 6h2v2H4V6zm0 4h2v2H4v-2zm0 4h2v2H4v-2zM18 6h2v2h-2V6zm0 4h2v2h-2v-2zm0 4h2v2h-2v-2z"/>
            </svg>
            QR Scanner
          </button>
        </div>
        <Suspense fallback={<ToolLoader />}>
          {activeTool === 'generator' ? <QRGenerator /> : <QRScanner />}
        </Suspense>
      </div>

      {/* ── AD MIDDLE ── */}
      <div className="container">
        <AdBanner slot="MIDDLE_RECT_SLOT" format="rectangle" />
      </div>

      {/* ── FEATURES ── */}
      <div className="container section">
        <SectionHeader
          tag="Features"
          title="Everything You Need"
          subtitle="Powerful features, completely free — no watermarks, no limits"
          align="center"
        />
        <div className={styles.featuresGrid}>
          {FEATURES.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.title} className={styles.featureCard}>
                <div className={styles.featureIcon}><Icon size={22} strokeWidth={1.75} /></div>
                <div className={styles.featureTitle}>{f.title}</div>
                <p className={styles.featureDesc}>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div className="container section">
        <div className={`card ${styles.howCard}`}>
          <SectionHeader
            tag="How It Works"
            title="Ready in Under 30 Seconds"
            subtitle="Generate your first QR code faster than making chai"
            align="center"
          />
          <div className={styles.steps}>
            {STEPS.map((s, i) => (
              <div key={s.n} className={styles.step}>
                <div className={styles.stepNum}>{s.n}</div>
                <div className={styles.stepTitle}>{s.title}</div>
                <p className={styles.stepDesc}>{s.desc}</p>
                {i < STEPS.length - 1 && <span className={styles.stepConnector} aria-hidden="true" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TESTIMONIALS ── */}
      <div className="container section">
        <SectionHeader
          tag="Reviews"
          title="What Users Say"
          subtitle="Trusted by businesses and individuals across India"
          align="center"
        />
        <div className={styles.testimonialsGrid}>
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className={styles.testimonialCard}>
              <div className={styles.testimonialStars} aria-label={`${t.stars} out of 5 stars`}>
                {'★'.repeat(t.stars)}{'☆'.repeat(5 - t.stars)}
              </div>
              <p className={styles.testimonialText}>{t.text}</p>
              <div className={styles.testimonialAuthor}>
                <div className={styles.testimonialAvatar}>{t.avatar}</div>
                <div>
                  <div className={styles.testimonialName}>{t.name}</div>
                  <div className={styles.testimonialRole}>{t.role} · {t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ ── */}
      <div className="container section">
        <SectionHeader
          tag="FAQ"
          title="Frequently Asked Questions"
          subtitle="Everything you need to know about SimplifyQR"
          align="center"
        />
        <div className={styles.faqGrid}>
          {FAQS.map(({ q, a }) => (
            <div key={q} className={styles.faqCard}>
              <div className={styles.faqQ}>{q}</div>
              <p className={styles.faqA}>{a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── BLOG PREVIEW ── */}
      <div className="container section">
        <SectionHeader
          tag="Blog"
          title="Guides & Tips"
          subtitle="Learn how to get the most out of QR codes"
          align="center"
        />
        <div className={styles.blogGrid}>
          {BLOG_PREVIEW.map(p => (
            <Link to="/blog" key={p.title} className={styles.blogCard}>
              <div className={styles.blogCardImg}>{p.icon}</div>
              <div className={styles.blogCardBody}>
                <span className="badge">{p.tag}</span>
                <div className={styles.blogCardTitle}>{p.title}</div>
                <p className={styles.blogCardExcerpt}>{p.excerpt}</p>
                <div className={styles.blogMeta}>
                  <span>{p.date}</span>
                  <span>{p.read} read</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <Link to="/blog" className="btn-outline">View All Articles →</Link>
        </div>
      </div>

      {/* ── AD BOTTOM ── */}
      <div className="container">
        <AdBanner slot="BOTTOM_BANNER_SLOT" format="horizontal" />
      </div>
    </>
  );
}
